import { collection, query, orderBy, limit, onSnapshot, where, Timestamp } from 'firebase/firestore'
import { db } from '../firebase'

/**
 * Diagnose service to handle real-time diagnostic messages from Firestore
 */
export class DiagnoseService {
  constructor(customerName) {
    this.customerName = customerName
    this.collectionName = `${customerName}_diagnose`
    this.listeners = []
  }

  /**
   * Get real-time diagnostic messages
   */
  subscribeToRealtimeDiagnose(callback, limitCount = 50) {
    const q = query(
      collection(db, this.collectionName),
      orderBy('dateTime', 'desc'),
      limit(limitCount)
    )

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const messages = []
      snapshot.forEach((doc) => {
        messages.push({
          id: doc.id,
          ...doc.data()
        })
      })
      callback(messages)
    }, (error) => {
      console.error('Error listening to diagnose messages:', error)
      callback([])
    })

    this.listeners.push(unsubscribe)
    return unsubscribe
  }

  /**
   * Get diagnostic messages for a specific device
   */
  subscribeToDeviceDiagnose(deviceId, callback, limitCount = 20) {
    const q = query(
      collection(db, this.collectionName),
      where('Device', '==', deviceId),
      orderBy('dateTime', 'desc'),
      limit(limitCount)
    )

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const messages = []
      snapshot.forEach((doc) => {
        messages.push({
          id: doc.id,
          ...doc.data()
        })
      })
      callback(messages)
    }, (error) => {
      console.error('Error listening to device diagnose messages:', error)
      callback([])
    })

    this.listeners.push(unsubscribe)
    return unsubscribe
  }

  /**
   * Get diagnostic messages by type
   */
  subscribeToDiagnoseByType(type, callback, limitCount = 30) {
    const q = query(
      collection(db, this.collectionName),
      where('Type', '==', type),
      orderBy('dateTime', 'desc'),
      limit(limitCount)
    )

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const messages = []
      snapshot.forEach((doc) => {
        messages.push({
          id: doc.id,
          ...doc.data()
        })
      })
      callback(messages)
    }, (error) => {
      console.error('Error listening to diagnose messages by type:', error)
      callback([])
    })

    this.listeners.push(unsubscribe)
    return unsubscribe
  }

  /**
   * Get diagnostic messages from last N hours
   */
  subscribeToRecentDiagnose(hours, callback, limitCount = 100) {
    const hoursAgo = new Date()
    hoursAgo.setHours(hoursAgo.getHours() - hours)
    
    const q = query(
      collection(db, this.collectionName),
      where('dateTime', '>=', Timestamp.fromDate(hoursAgo)),
      orderBy('dateTime', 'desc'),
      limit(limitCount)
    )

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const messages = []
      snapshot.forEach((doc) => {
        messages.push({
          id: doc.id,
          ...doc.data()
        })
      })
      callback(messages)
    }, (error) => {
      console.error('Error listening to recent diagnose messages:', error)
      callback([])
    })

    this.listeners.push(unsubscribe)
    return unsubscribe
  }

  /**
   * Get diagnostic summary/statistics
   */
  async getDiagnoseSummary() {
    try {
      // This would need aggregation queries or client-side processing
      // For now, we'll return a placeholder structure
      return {
        totalMessages: 0,
        errorCount: 0,
        warningCount: 0,
        infoCount: 0,
        criticalCount: 0,
        lastUpdate: null
      }
    } catch (error) {
      console.error('Error getting diagnose summary:', error)
      throw error
    }
  }

  /**
   * Clean up all listeners
   */
  cleanup() {
    this.listeners.forEach(unsubscribe => unsubscribe())
    this.listeners = []
  }
}

/**
 * Factory function to create diagnose service based on customer name
 */
export function createDiagnoseService(customerName) {
  if (!customerName) {
    console.warn('No customer name provided for diagnose service')
    return null
  }
  
  return new DiagnoseService(customerName)
}